import { ReactElement } from 'react'

import BellIcon from '@heroicons/react/outline/esm/BellIcon.js'
import ChatAlt2Icon from '@heroicons/react/outline/esm/ChatAlt2Icon.js'
import HomeIcon from '@heroicons/react/outline/esm/HomeIcon.js'
import NewspaperIcon from '@heroicons/react/outline/esm/NewspaperIcon.js'
import PlusIcon from '@heroicons/react/outline/esm/PlusIcon.js'
import SearchIcon from '@heroicons/react/outline/esm/SearchIcon.js'
import UserCircleIcon from '@heroicons/react/outline/esm/UserCircleIcon.js'
import UserGroupIcon from '@heroicons/react/outline/esm/UserGroupIcon.js'
import UserIcon from '@heroicons/react/outline/esm/UserIcon.js'
import ViewGridIcon from '@heroicons/react/outline/esm/ViewGridIcon.js'
import BellSolidIcon from '@heroicons/react/solid/esm/BellIcon.js'
import ChatAlt2SolidIcon from '@heroicons/react/solid/esm/ChatAlt2Icon.js'
import HomeSolidIcon from '@heroicons/react/solid/esm/HomeIcon.js'
import NewspaperSolidIcon from '@heroicons/react/solid/esm/NewspaperIcon.js'
import PlusSolidIcon from '@heroicons/react/solid/esm/PlusIcon.js'
import SearchSolidIcon from '@heroicons/react/solid/esm/SearchIcon.js'
import UserCircleSolidIcon from '@heroicons/react/solid/esm/UserCircleIcon.js'
import UserGroupSolidIcon from '@heroicons/react/solid/esm/UserGroupIcon.js'
import UserSolidIcon from '@heroicons/react/solid/esm/UserIcon.js'
import ViewGridSolidIcon from '@heroicons/react/solid/esm/ViewGridIcon.js'

import { Network } from '@tribeplatform/gql-client/types'
import { MemberAvatarPlaceholder } from '@tribeplatform/react-components/common/components/member'
import { Avatar } from '@tribeplatform/react-ui-kit/Avatar'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { TopBar } from '@tribeplatform/react-ui-kit/TopBar'

import {
  NavbarControlItem,
  NavbarControlType,
  NavbarNavigationAction,
  NavbarNavigationItem,
  NavbarStyle,
} from './constants.js'

const icons = {
  bell: BellIcon,
  chat: ChatAlt2Icon,
  plus: PlusIcon,
  home: HomeIcon,
  newspaper: NewspaperIcon,
  search: SearchIcon,
  userCircle: UserCircleIcon,
  userGroup: UserGroupIcon,
  viewGrid: ViewGridIcon,
  user: UserIcon,
}

const activeIcons = {
  bell: BellSolidIcon,
  chat: ChatAlt2SolidIcon,
  plus: PlusSolidIcon,
  home: HomeSolidIcon,
  newspaper: NewspaperSolidIcon,
  search: SearchSolidIcon,
  userCircle: UserCircleSolidIcon,
  userGroup: UserGroupSolidIcon,
  viewGrid: ViewGridSolidIcon,
  user: UserSolidIcon,
}

export const getIcon = (iconName: string, active = false) => {
  if (!iconName) {
    return null
  }
  if (active) {
    return activeIcons[iconName]
  }
  return icons[iconName]
}

export const getOldNavigationItems = (
  network: Network,
): NavbarNavigationItem[] => {
  const { topNavigation } = network || {}
  const { enabled = false, items } = topNavigation || {}

  if (!enabled) {
    return []
  }

  return items
    .filter(item => item.link)
    .map(item => ({
      label: item.text,
      action: NavbarNavigationAction.OPEN_LINK,
      link: item.link,
      openInNewWindow: item.openInNewWindow || false,
    }))
}

export const getCleanNavbarProps = (options: {
  network: Network
  props?: {
    navigationItems?: NavbarNavigationItem[]
    view?: NavbarStyle
  }
}): {
  navigationItems: NavbarNavigationItem[]
  view: NavbarStyle
} => {
  const { network, props = {} } = options
  const { navigationItems: newNavigationItems, view: newView } = props
  const navigationItems: NavbarNavigationItem[] = (
    newNavigationItems || getOldNavigationItems(network)
  ).filter(item => Object.values(NavbarNavigationAction).includes(item.action))

  let view = newView
  if (!view && navigationItems.length) {
    view = NavbarStyle.STACKED
  } else if (!view) {
    view = NavbarStyle.DEFAULT
  }

  return {
    navigationItems,
    view,
  }
}

export const getNavbarControlPresentation = (options: {
  item: NavbarControlItem
  avatarUrl?: string
  avatarName?: string
  props: Record<string, any>
}): ReactElement => {
  const { item, avatarUrl, avatarName, props } = options
  const Icon = getIcon(item.icon)

  switch (item.type) {
    case NavbarControlType.BUTTON:
      if (props.rounded) {
        return (
          <TopBar.Button
            key={item.label}
            icon={Icon ? <Icon className="h-5 w-5" /> : null}
            rounded
            {...props}
          />
        )
      }

      return (
        <Button
          key={item.label}
          trailingIcon={Icon ? <Icon className="h-5 w-5" /> : null}
          {...props}
        >
          {item.showLabel ? item.label : null}
        </Button>
      )

    case NavbarControlType.ICON:
      return (
        <span>
          <span className="sr-only">{item.label}</span>
          {Icon && <Icon className="h-7 w-7" aria-hidden="true" />}
        </span>
      )
    case NavbarControlType.AVATAR:
      return (
        <span>
          <span className="sr-only">{item.label}</span>
          <Avatar
            src={avatarUrl}
            name={avatarName}
            fallback={MemberAvatarPlaceholder}
          />
        </span>
      )
    default:
      return null
  }
}
